import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import {
  AddpCompanyDetails,
  PopSummary,
  ServiceDeliveryLocationDetails,
  ServiceDeliveryLocationsService,
  ServiceItem
} from '../../../../services/service-delivery-locations.service';
import { CompanySummary } from '../../../../models/company-summary';
import { CartService } from '../../../../services/cart.service';
import { UtilsService } from '../../../../appSharedModule/services/utils.service';
import { XcoGroup, XcoGroupItem } from '../../models/xco-group';
import { Subscription } from 'rxjs';

type DialogStep = 'selection' | 'form';

@Component({
  selector: 'app-addp-dialog',
  templateUrl: './addp-dialog.component.html',
  styleUrls: ['./addp-dialog.component.scss']
})
export class AddpDialogComponent implements OnChanges, OnDestroy {
  @Input() company: CompanySummary | null = null;
  @Input() pop: PopSummary | ServiceDeliveryLocationDetails['commercialPop'] | null = null;
  @Input() group: XcoGroup | null = null;
  @Input() items: XcoGroupItem[] = [];
  @Input() visible = false;

  @Output() visibleChange = new EventEmitter<boolean>();

  availableServices: { key: string; label: string }[] = [];
  selectedServiceKey: string | null = null;
  selectedItemIds: Set<number> = new Set();
  step: DialogStep = 'selection';
  addpDetails: AddpCompanyDetails | null = null;
  loadingDetails = false;
  detailsError: string | null = null;
  submittingOrder = false;
  submissionError: string | null = null;

  private detailsSub: Subscription | null = null;
  private submissionSub: Subscription | null = null;

  constructor(
    private readonly service: ServiceDeliveryLocationsService,
    private readonly cartService: CartService,
    private readonly utilsService: UtilsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && !this.visible) {
      this.resetState();
    }

    if (changes['items']) {
      this.buildAvailableServices();
      if (this.availableServices.length === 1) {
        this.selectService(this.availableServices[0].key);
      } else {
        this.selectedServiceKey = null;
        this.selectedItemIds.clear();
      }
    }
  }

  ngOnDestroy(): void {
    this.detailsSub?.unsubscribe();
    this.submissionSub?.unsubscribe();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetState();
  }

  get selectedServiceLabel(): string {
    return this.availableServices.find(s => s.key === this.selectedServiceKey)?.label || '';
  }

  get popDisplayId(): string {
    return this.pop?.displayId || '';
  }

  get popName(): string {
    return this.pop?.name || '';
  }

  get xcoTechnicalPop(): string {
    for (const entry of this.items) {
      const crossConnect = entry.item.crossConnects?.find(cc => cc.crossConnectId === this.group?.xcoId);
      const technicalPop = crossConnect?.technicalPop;
      if (technicalPop?.technicalId) {
        return technicalPop.technicalId;
      }
      if (technicalPop?.name) {
        return technicalPop.name;
      }
    }
    return '';
  }

  get unselectedServicesLabel(): string {
    if (!this.selectedServiceKey) {
      return '';
    }
    return this.availableServices
      .filter(s => s.key !== this.selectedServiceKey)
      .map(s => s.label)
      .join(', ');
  }

  get selectedItems(): ServiceItem[] {
    if (!this.selectedServiceKey) {
      return [];
    }
    return this.items
      .filter(entry => entry.serviceKey === this.selectedServiceKey && this.selectedItemIds.has(entry.item.id))
      .map(entry => entry.item);
  }

  selectService(serviceKey: string): void {
    this.selectedServiceKey = serviceKey;
    this.selectedItemIds.clear();
    this.items
      .filter(entry => entry.serviceKey === serviceKey)
      .forEach(entry => this.selectedItemIds.add(entry.item.id));
  }

  toggleItem(itemId: number): void {
    if (this.selectedItemIds.has(itemId)) {
      this.selectedItemIds.delete(itemId);
    } else {
      this.selectedItemIds.add(itemId);
    }
  }

  addToCart(): void {
    if (this.selectedItemIds.size === 0) {
      return;
    }
    this.step = 'form';
    this.loadAddpCompanyDetails();
  }

  handleCartCancel(): void {
    this.close();
  }

  handleCartSubmit(payload: any): void {
    if (this.submittingOrder) {
      return;
    }
    this.submissionError = null;
    this.submittingOrder = true;
    this.submissionSub?.unsubscribe();
    this.submissionSub = this.cartService.saveAddpItem(payload).subscribe({
      next: () => {
        this.submittingOrder = false;
        const detail = 'Your request has been added to your cart.';
        this.utilsService.displayMessage('Extra XCO (ADDP) request saved', detail, 'success', false, false, {
          viewCartLink: true,
          disableCustomClose: true
        });
        this.cartService.refreshCart();
        this.close();
      },
      error: () => {
        this.submittingOrder = false;
        this.submissionError = 'Unable to save this Extra XCO (ADDP) request. Please try again later.';
        this.utilsService.displayMessage('Unable to save Extra XCO (ADDP) request', this.submissionError, 'error');
      }
    });
  }

  private resetState(): void {
    this.step = 'selection';
    this.selectedServiceKey = null;
    this.selectedItemIds.clear();
    this.addpDetails = null;
    this.detailsError = null;
    this.submittingOrder = false;
    this.submissionError = null;
    this.loadingDetails = false;
    this.detailsSub?.unsubscribe();
    this.detailsSub = null;
    this.submissionSub?.unsubscribe();
    this.submissionSub = null;
  }

  private buildAvailableServices(): void {
    const servicesMap = new Map<string, string>();
    this.items.forEach(entry => {
      servicesMap.set(entry.serviceKey, entry.serviceLabel);
    });
    this.availableServices = Array.from(servicesMap.entries()).map(([key, label]) => ({ key, label }));
  }

  private loadAddpCompanyDetails(): void {
    if (!this.company?.id || this.addpDetails || this.loadingDetails) {
      return;
    }
    this.loadingDetails = true;
    this.detailsError = null;
    this.detailsSub = this.service.getAddpCompanyDetails(this.company.id).subscribe({
      next: details => {
        this.addpDetails = details;
        this.loadingDetails = false;
      },
      error: () => {
        this.detailsError = 'Unable to load company details for this quote.';
        this.loadingDetails = false;
      }
    });
  }
}
